/**
 * Tiny score chip for the JobsList Quality column.
 * Hovering shows a popover with each deduction reason so the recruiter
 * knows exactly what to fix to bring the score up.
 *
 * The popover is rendered into document.body via a React portal so it can
 * escape any `overflow: auto/hidden` ancestor (e.g. the DataTable scroll
 * container, which previously clipped it from view).
 */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";

const TIER_STYLE = {
    great: "border-emerald-300 bg-emerald-50 text-emerald-800",
    good: "border-blue-300 bg-blue-50 text-blue-800",
    fair: "border-amber-300 bg-amber-50 text-amber-800",
    poor: "border-rose-300 bg-rose-50 text-rose-800",
};

export default function JobQualityBadge({ q }) {
    const [hover, setHover] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const anchorRef = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        if (!hover || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const POP_W = 320;
        // Clamp inside viewport
        const left = Math.min(Math.max(8, rect.left), window.innerWidth - POP_W - 8);
        setCoords({ top: rect.bottom + 4, left });
    }, [hover]);

    if (!q) {
        return <span className="text-[10px] font-mono text-muted-foreground">—</span>;
    }
    const tier = TIER_STYLE[q.tier] || TIER_STYLE.fair;

    return (
        <>
            <span
                ref={anchorRef}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-mono uppercase tracking-widest cursor-help ${tier}`}
                onMouseEnter={() => { clearTimeout(timer.current); setHover(true); }}
                onMouseLeave={() => { timer.current = setTimeout(() => setHover(false), 140); }}
                data-testid={`job-quality-${q.score}`}
            >
                <Sparkles className="w-2.5 h-2.5" /> {q.score} · {q.label}
            </span>
            {hover && (q.issues || []).length > 0 && createPortal(
                <div
                    className="fixed z-[100] w-80 max-w-[calc(100vw-16px)] border border-border bg-white shadow-xl p-3 text-xs text-foreground"
                    style={{ top: coords.top, left: coords.left }}
                    onMouseEnter={() => clearTimeout(timer.current)}
                    onMouseLeave={() => { timer.current = setTimeout(() => setHover(false), 140); }}
                    data-testid="job-quality-popover"
                >
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb mb-1.5">
                        Why this score — {100 - q.score} point{100 - q.score === 1 ? "" : "s"} deducted
                    </div>
                    <ul className="space-y-1">
                        {q.issues.map((it, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <span className="font-mono text-[10px] text-rose-700 min-w-[28px] text-right shrink-0">−{it.points}</span>
                                <span className="text-foreground/80 text-[12px]">{it.reason}</span>
                            </li>
                        ))}
                    </ul>
                    {q.score === 100 && (
                        <div className="text-[12px] text-emerald-700 mt-1">Looks great — nothing flagged.</div>
                    )}
                </div>,
                document.body,
            )}
        </>
    );
}
